import { useState, type ReactElement } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Info,
} from 'lucide-react';
import type {
  AlertSourceDetails as AlertSourceDetailsType,
  AlertSourceField,
  PiiMatchedType,
} from '@kirin/api-client/contracts/alerts';
import { ALERT_SOURCE_FIELD_KEYS } from '@kirin/api-client/contracts/alerts';
import { COPY_FIELD_NO_VALUE_REASON } from '@kirin/api-client/helpers/source-details';
import { osvAdvisoryUrl } from '@/lib/osv';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import {
  EXTENSIONS_ALLOW_BLOCK_CONFIG,
  MCP_ALLOW_BLOCK_CONFIG,
} from '@/components/policylist/allow-block/config';
import type { AllowBlockPolicyUiConfig } from '@/components/policylist/allow-block/types';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';

interface AlertSourceDetailsProps {
  sourceDetails: AlertSourceDetailsType;
  className?: string;
  /**
   * KIR-4481: suppress the component's own "DETECTED ON CLIENT" heading and top
   * spacing when it is embedded in the grouped-cards panel, whose card title
   * already labels the section. Defaults to false (legacy standalone layout).
   */
  hideHeading?: boolean;
  /**
   * KIR-4481: suppress the MCP `configJsonBlock` — the grouped-cards panel shows
   * it in a dedicated "Server definition (JSON)" card instead. The matched-text
   * evidence in the same "More details" toggle is unaffected. Defaults to false.
   */
  hideConfigJson?: boolean;
}

/**
 * Clarifies that the MCP Name is the config-file key (not the runtime handshake
 * name), so it's clear which value to copy into an Allow/Block rule.
 */
const MCP_NAME_HINT =
  'MCP server name as appears in the MCP server configuration file';

/**
 * KIR-4230: the code-scan snippet is a window around the match (the matched
 * line is prefixed `>>>`), not the whole file — say so, since a reader
 * otherwise cannot tell whether they are seeing everything the rule saw.
 */
const MATCHED_CODE_HINT =
  'The code around the line that matched the rule. The matching line is prefixed with ">>>". Secrets are masked.';

/**
 * Resolve the human-readable label for a source-detail field straight from the
 * Allow/Block column config (KIR-3529), so the alert panel reads identically to
 * the editor and the wording stays single-sourced. The backend deliberately
 * carries only stable `key`s (`name`/`publisher`/`version`/`type`); the mapping
 * from those keys to the editor's column ids lives here.
 */
function labelFromConfig(
  config: AllowBlockPolicyUiConfig,
  columnId: string,
  fallback: string
): string {
  return (
    config.columns.find(column => column.id === columnId)?.headerLabel ??
    fallback
  );
}

function extensionFieldLabel(key: string): string {
  switch (key) {
    case ALERT_SOURCE_FIELD_KEYS.NAME:
      return labelFromConfig(
        EXTENSIONS_ALLOW_BLOCK_CONFIG,
        'extensionId',
        'Extension Name'
      );
    case ALERT_SOURCE_FIELD_KEYS.VERSION:
      return labelFromConfig(
        EXTENSIONS_ALLOW_BLOCK_CONFIG,
        'version',
        'Version'
      );
    case ALERT_SOURCE_FIELD_KEYS.PUBLISHER:
      return labelFromConfig(
        EXTENSIONS_ALLOW_BLOCK_CONFIG,
        'publisher',
        'Publisher'
      );
    default:
      return key;
  }
}

function mcpFieldLabel(key: string): string {
  switch (key) {
    case ALERT_SOURCE_FIELD_KEYS.NAME:
      return labelFromConfig(
        MCP_ALLOW_BLOCK_CONFIG,
        'mcpNameOrId',
        'MCP Server Name'
      );
    case ALERT_SOURCE_FIELD_KEYS.PUBLISHER:
      return labelFromConfig(MCP_ALLOW_BLOCK_CONFIG, 'publisher', 'Publisher');
    case ALERT_SOURCE_FIELD_KEYS.TYPE:
      return labelFromConfig(MCP_ALLOW_BLOCK_CONFIG, 'transport', 'Type');
    default:
      return key;
  }
}

// Package rules are managed from the Packages page, not the Allow/Block list
// editor, so there is no column config to borrow these labels from.
function packageFieldLabel(key: string): string {
  switch (key) {
    case ALERT_SOURCE_FIELD_KEYS.NAME:
      return 'Package Name';
    case ALERT_SOURCE_FIELD_KEYS.ECOSYSTEM:
      return 'Registry';
    case ALERT_SOURCE_FIELD_KEYS.VERSION:
      return 'Version';
    default:
      return key;
  }
}

/** Field-label resolver per entity kind; total so a new kind cannot fall through. */
const FIELD_LABEL_BY_KIND: Record<
  Exclude<AlertSourceDetailsType['kind'], 'pii'>,
  (key: string) => string
> = {
  extension: extensionFieldLabel,
  mcp: mcpFieldLabel,
  package: packageFieldLabel,
};

/**
 * Type values copy as the Allow/Block rule value (lowercase `local`/`remote`/
 * `any`) but display title-cased to match the editor's Type select labels.
 */
function formatDisplayValue(key: string, value: string): string {
  if (key === ALERT_SOURCE_FIELD_KEYS.TYPE && value.length > 0) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}

/** Disabled per-field copy affordance, wrapped in a tooltip explaining why. */
function DisabledCopy({ reason }: { reason: string }): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled
            className="h-6 w-6 shrink-0 p-0"
            aria-label={reason}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}

/** Small info affordance explaining what a field's value represents. */
function FieldHint({ hint }: { hint: string }): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          role="img"
          aria-label={hint}
          className="inline-flex shrink-0 text-muted-foreground"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

/**
 * KIR-4070: the "Advisory" row for a "Vulnerable dependency in extension"
 * finding — the CVE/GHSA id plus a working link to its OSV.dev advisory. The
 * id itself is otherwise dropped/masked before it reaches the panel.
 */
function AdvisoryRow({ osvId }: { osvId: string }): ReactElement {
  return (
    <div className="flex items-start gap-2 py-1">
      <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
        Advisory
      </div>
      <div className="flex min-h-6 min-w-0 flex-1 items-center gap-2">
        <a
          href={osvAdvisoryUrl(osvId)}
          target="_blank"
          rel="noopener noreferrer"
          title={`View ${osvId} on OSV.dev`}
          aria-label={`View ${osvId} on OSV.dev`}
          className="inline-flex min-w-0 items-center gap-0.5 font-mono text-xs text-primary break-all hover:underline"
        >
          {osvId}
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
        <CopyToClipboardButton
          text={osvId}
          variant="ghost"
          size="sm"
          compactCopiedState
          className="h-6 w-6 shrink-0 p-0"
          aria-label="Copy advisory id"
        >
          <span className="sr-only">Copy advisory id</span>
        </CopyToClipboardButton>
      </div>
    </div>
  );
}

function SourceFieldRow({
  label,
  field,
  hint,
}: {
  label: string;
  field: AlertSourceField;
  hint?: string;
}): ReactElement {
  const detected =
    field.detected != null
      ? formatDisplayValue(field.key, field.detected)
      : null;
  const allowBlock =
    field.allowBlockValue != null
      ? formatDisplayValue(field.key, field.allowBlockValue)
      : null;

  // The Type row leads with the Local/Remote classification (the Allow/Block
  // enum, shown in black) and surfaces the underlying transport method
  // (http/stdio/sse) beneath it. Every other field leads with the detected
  // value and reveals the Allow/Block-compatible value beneath only when the
  // two differ.
  const isType = field.key === ALERT_SOURCE_FIELD_KEYS.TYPE;
  const primary = isType ? (allowBlock ?? detected) : (detected ?? allowBlock);
  const method = isType ? field.detected?.trim() || null : null;
  const showAllowBlock =
    !isType &&
    detected != null &&
    allowBlock != null &&
    detected !== allowBlock;

  // The Allow/Block editor exposes `type` as a dropdown (local/remote/any), not
  // a free-text field, so there is nothing to paste — no copy affordance.
  const copyable = field.key !== ALERT_SOURCE_FIELD_KEYS.TYPE;
  const canCopy =
    copyable &&
    !field.isUnknown &&
    field.allowBlockValue != null &&
    field.allowBlockValue.trim().length > 0;

  return (
    <div className="flex items-start gap-2 py-1">
      <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
        {label}
      </div>
      <div className="min-w-0 flex-1">
        {field.isUnknown || primary == null ? (
          <span className="inline-flex min-h-6 items-center gap-1">
            <span className="text-xs italic text-muted-foreground">
              Unknown
            </span>
            {hint && <FieldHint hint={hint} />}
            {copyable && <DisabledCopy reason={COPY_FIELD_NO_VALUE_REASON} />}
          </span>
        ) : (
          <div className="min-w-0">
            <div className="flex min-h-6 min-w-0 items-center gap-1">
              <span
                className="font-mono text-xs break-all"
                title={field.masked ? 'Sensitive fragments masked' : undefined}
              >
                {primary}
              </span>
              {hint && <FieldHint hint={hint} />}
              {copyable &&
                (canCopy ? (
                  <CopyToClipboardButton
                    // Copy the Allow/Block-compatible value (never the masked
                    // display / "Unknown") so it pastes straight into the editor
                    // and matches.
                    text={field.allowBlockValue ?? ''}
                    variant="ghost"
                    size="sm"
                    compactCopiedState
                    className="h-6 w-6 shrink-0 p-0"
                    aria-label={`Copy ${label}`}
                  >
                    <span className="sr-only">{`Copy ${label}`}</span>
                  </CopyToClipboardButton>
                ) : (
                  <DisabledCopy reason={COPY_FIELD_NO_VALUE_REASON} />
                ))}
            </div>
            {showAllowBlock && (
              <div className="font-mono text-[11px] text-muted-foreground break-all">
                Allow/Block value: {allowBlock}
              </div>
            )}
            {method && (
              <div className="font-mono text-[11px] text-muted-foreground break-all">
                Method: {method}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface PiiMatchedTypesProps {
  matchedTypes?: PiiMatchedType[];
  totalFindings?: number;
  truncated?: boolean;
}

/**
 * "MATCHED PII" breakdown (KIR-4211). The redacted excerpt below it is windowed
 * around the FIRST match, so on a large payload it reads as unrelated content
 * with no way to tell what fired the alert — this row states it outright.
 * Sensitive types are called out because they are what lifts the alert to the
 * High "Multiple PIIs detected" tier; a High alert whose only sensitive entry is
 * a single loose match is the shape of a false positive.
 *
 * Renders nothing for alerts recorded before KIR-4211 — they carry no breakdown,
 * and an empty header would read as "nothing matched".
 */
function PiiMatchedTypes({
  matchedTypes,
  totalFindings,
  truncated,
}: PiiMatchedTypesProps): ReactElement | null {
  if (!matchedTypes || matchedTypes.length === 0) {
    return null;
  }

  const total =
    totalFindings ?? matchedTypes.reduce((sum, t) => sum + t.count, 0);

  return (
    <div className="mb-3">
      <div className="mb-2">
        <span className="text-xs font-semibold tracking-wide text-muted-foreground">
          {/* `truncated` means DLP capped the scan, so the total is a lower bound. */}
          MATCHED PII ({total}
          {truncated ? '+' : ''})
        </span>
      </div>
      {/* The count is of DISTINCT values (KIR-4429): the same value repeated in
          the scanned content — e.g. one author email across a git-log read —
          collapses to a single PII, so a repeated value never inflates the tier. */}
      <p className="mb-2 text-xs text-muted-foreground">
        Duplicate values are counted once — this is the number of unique PIIs.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {matchedTypes.map(type => (
          <span
            key={type.label}
            className={cn(
              'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs',
              type.sensitive
                ? 'border-destructive/40 bg-destructive/10 font-medium text-destructive'
                : 'border-border bg-muted text-muted-foreground'
            )}
            title={
              type.sensitive
                ? 'Sensitive type — drives the alert severity'
                : 'Non-sensitive type'
            }
          >
            {type.label} ×{type.count}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * "DETECTED ON CLIENT" panel (KIR-3529): the masked Extension / MCP source
 * details Kirin matched against, rendered with the same identity field labels
 * the Allow/Block editor uses so a value can be copied off the alert and pasted
 * straight into the editor. For MCP findings a "More details" toggle reveals
 * the masked JSON block that defines the server.
 */
export function AlertSourceDetails({
  sourceDetails,
  className,
  hideHeading = false,
  hideConfigJson = false,
}: AlertSourceDetailsProps): ReactElement {
  const [showJson, setShowJson] = useState(false);
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
  ]);
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;

  // PII detection (KIR-3034): PII alerts carry a redacted content excerpt
  // instead of an Allow/Block entity. Every source carries one, file reads
  // included (KIR-4210) — the "chat-side alerts only" framing this comment used
  // to describe was inverted there. Every *detected* PII value is already
  // replaced by `[REDACTED:<type>]` backend-side; types the admin disabled are
  // never matched, so they are not masked.
  if (sourceDetails.kind === 'pii') {
    return (
      <div className={cn(hideHeading ? '' : 'mt-3 border-t pt-3', className)}>
        <PiiMatchedTypes
          matchedTypes={sourceDetails.matchedTypes}
          totalFindings={sourceDetails.totalFindings}
          truncated={sourceDetails.truncated}
        />
        <div className="mb-2">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground">
            DETECTED CONTENT (PII REDACTED)
          </span>
        </div>
        <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-words whitespace-pre-wrap dark:bg-slate-700">
          {sourceDetails.redactedSample}
        </pre>
      </div>
    );
  }

  const labelFor = FIELD_LABEL_BY_KIND[sourceDetails.kind];

  const configJsonBlock =
    sourceDetails.kind === 'mcp' ? sourceDetails.configJsonBlock : undefined;

  return (
    <div className={cn(hideHeading ? '' : 'mt-3 pt-3', className)}>
      {!hideHeading && (
        <div className="mb-2">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground">
            DETECTED ON CLIENT
          </span>
          {capabilityDetectionEnabled &&
            sourceDetails.kind === 'extension' &&
            sourceDetails.publisherTrusted === false && (
              <span className="ml-2 text-xs font-medium text-destructive">
                (not trusted)
              </span>
            )}
        </div>
      )}
      {/* KIR-4481: with the heading hidden the "(not trusted)" note has no host,
          so surface it as its own line just above the fields. */}
      {hideHeading &&
        capabilityDetectionEnabled &&
        sourceDetails.kind === 'extension' &&
        sourceDetails.publisherTrusted === false && (
          <div className="mb-2 text-xs font-medium text-destructive">
            (not trusted)
          </div>
        )}

      <div className="min-w-0">
        {sourceDetails.fields.map(field => (
          <SourceFieldRow
            key={field.key}
            label={labelFor(field.key)}
            field={field}
            hint={
              sourceDetails.kind === 'mcp' &&
              field.key === ALERT_SOURCE_FIELD_KEYS.NAME
                ? MCP_NAME_HINT
                : undefined
            }
          />
        ))}

        {sourceDetails.sourceFile && (
          <div className="flex items-start gap-2 py-1">
            <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
              Source file
            </div>
            <div className="flex min-h-6 min-w-0 flex-1 items-center font-mono text-xs break-all">
              {sourceDetails.sourceFile.display}
              {sourceDetails.sourceFile.ide && (
                <span className="text-muted-foreground">
                  {' '}
                  ({sourceDetails.sourceFile.ide})
                </span>
              )}
            </div>
          </div>
        )}

        {sourceDetails.kind === 'package' && sourceDetails.typosquatTarget && (
          <div className="flex items-start gap-2 py-1">
            <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
              Imitates
            </div>
            <div className="flex min-h-6 min-w-0 flex-1 items-center font-mono text-xs break-all">
              {sourceDetails.typosquatTarget}
            </div>
          </div>
        )}

        {(sourceDetails.kind === 'extension' ||
          sourceDetails.kind === 'package') &&
          sourceDetails.osvId && <AdvisoryRow osvId={sourceDetails.osvId} />}
      </div>

      {sourceDetails.kind === 'mcp' &&
        ((configJsonBlock && !hideConfigJson) || sourceDetails.matchedText) && (
          <div className="mt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-1 text-xs"
              aria-expanded={showJson}
              onClick={() => setShowJson(prev => !prev)}
            >
              {showJson ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
              {showJson ? 'Hide details' : 'More details'}
            </Button>
            {showJson && (
              <div className="mt-1 min-w-0">
                {/* KIR-4175: detection evidence — the rule that fired, the
                    matched span highlighted in context, and the untruncated
                    description. Secrets are already redacted backend-side. */}
                {sourceDetails.matchedText && (
                  <div className="mb-2">
                    {sourceDetails.matchedRule && (
                      <div className="flex items-start gap-2 py-0.5">
                        <div className="w-28 shrink-0 text-xs text-muted-foreground">
                          Rule
                        </div>
                        <div className="min-w-0 flex-1 text-xs">
                          {sourceDetails.matchedRule}
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-2 py-0.5">
                      <div className="w-28 shrink-0 text-xs text-muted-foreground">
                        Matched text
                      </div>
                      <div className="min-w-0 flex-1 font-mono text-xs break-all">
                        {sourceDetails.matchedText}
                      </div>
                    </div>
                    {sourceDetails.toolDescription && (
                      <div className="flex flex-col gap-1 py-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          Full description
                          <CopyToClipboardButton
                            text={sourceDetails.toolDescription}
                            variant="ghost"
                            size="sm"
                            compactCopiedState
                            className="h-6 px-2 text-xs"
                            aria-label="Copy full tool description"
                          >
                            Copy
                          </CopyToClipboardButton>
                        </div>
                        <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-words whitespace-pre-wrap dark:bg-slate-700">
                          {sourceDetails.toolDescription}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                {configJsonBlock && !hideConfigJson && (
                  <div>
                    <div className="mb-1 text-xs text-muted-foreground">
                      JSON block that defines this MCP server:
                    </div>
                    <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs dark:bg-slate-700">
                      {configJsonBlock}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      {/* KIR-4230: extension code-scan evidence. Mirrors the MCP block above
          (same "More details" disclosure, same Rule row) but renders the
          matched code as a multi-line <pre> rather than a single wrapped span
          — a YARA hit is a code excerpt, not a one-line pattern match, and on
          a minified bundle it is a windowed slice of one very long line.
          `matchedText` is secret-redacted and length-capped backend-side. */}
      {sourceDetails.kind === 'extension' &&
        (sourceDetails.matchedRule || sourceDetails.matchedText) && (
          <div className="mt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-1 text-xs"
              aria-expanded={showJson}
              onClick={() => setShowJson(prev => !prev)}
            >
              {showJson ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
              {showJson ? 'Hide details' : 'More details'}
            </Button>
            {showJson && (
              <div className="mt-1 min-w-0" data-testid="code-scan-evidence">
                {sourceDetails.matchedRule && (
                  <div className="flex items-start gap-2 py-0.5">
                    <div className="w-28 shrink-0 text-xs text-muted-foreground">
                      Rule
                    </div>
                    <div className="min-w-0 flex-1 font-mono text-xs break-all">
                      {sourceDetails.matchedRule}
                    </div>
                  </div>
                )}
                {sourceDetails.matchedLine != null && (
                  <div className="flex items-start gap-2 py-0.5">
                    <div className="w-28 shrink-0 text-xs text-muted-foreground">
                      Line
                    </div>
                    <div className="min-w-0 flex-1 font-mono text-xs">
                      {sourceDetails.matchedLine}
                    </div>
                  </div>
                )}
                {sourceDetails.matchedText && (
                  <div className="flex flex-col gap-1 py-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      Matched code
                      <FieldHint hint={MATCHED_CODE_HINT} />
                      <CopyToClipboardButton
                        text={sourceDetails.matchedText}
                        variant="ghost"
                        size="sm"
                        compactCopiedState
                        className="h-6 px-2 text-xs"
                        aria-label="Copy matched code"
                      >
                        Copy
                      </CopyToClipboardButton>
                    </div>
                    {/* Same styling as the secret panel's "Context" block so
                        both redacted-excerpt surfaces read identically. */}
                    <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-words whitespace-pre-wrap dark:bg-slate-700">
                      {sourceDetails.matchedText}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
    </div>
  );
}

AlertSourceDetails.displayName = 'AlertSourceDetails';
